import { canvasTextStyle } from "../node-style";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";
import { referenceTargetKey } from "../domain/canvas";
import type { CanvasNode } from "../domain/types";
import type { CanvasAssetResolver, ReferenceResolution } from "../references";
import {
  CanvasNodeContent,
  canvasNumberProp,
  canvasStringProp,
} from "./CanvasNodeContent";
import type { CanvasResizeHandle } from "./useCanvasInteractions";
import { useCanvasLocale, useCanvasMessages } from "./i18n";

function rotation(node: CanvasNode): string | undefined {
  if (node.geometry.rotation === 0) return undefined;
  const centerX = node.geometry.x + node.geometry.width / 2;
  const centerY = node.geometry.y + node.geometry.height / 2;
  return `rotate(${node.geometry.rotation} ${centerX} ${centerY})`;
}

interface InlineTextEditorProps {
  node: CanvasNode;
  value: string;
  onCommit(value: string): void;
  onCancel(): void;
}

function InlineTextEditor({
  node,
  value,
  onCommit,
  onCancel,
}: InlineTextEditorProps) {
  const messages = useCanvasMessages();
  const locale = useCanvasLocale();
  const [draft, setDraft] = useState(value);
  const composing = useRef(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  useEffect(() => textarea.current?.focus(), []);
  return (
    <foreignObject
      x={node.geometry.x}
      y={node.geometry.y}
      width={Math.max(80, node.geometry.width)}
      height={Math.max(50, node.geometry.height)}
    >
      <textarea
        ref={textarea}
        value={draft}
        aria-label={messages.editText}
        lang={locale === "zh" ? "zh-CN" : locale}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={() => {
          composing.current = false;
        }}
        onBlur={() => onCommit(draft)}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
          if (event.key === "Escape" && !composing.current) {
            event.preventDefault();
            event.stopPropagation();
            onCancel();
          }
          if (
            event.key === "Enter" &&
            (event.metaKey || event.ctrlKey) &&
            !composing.current
          ) {
            event.preventDefault();
            event.stopPropagation();
            onCommit(draft);
          }
        }}
        style={{
          boxSizing: "border-box",
          width: "100%",
          height: "100%",
          resize: "none",
          padding: 12,
          border: "2px solid #4f46e5",
          borderRadius: 8,
          outline: 0,
          color: canvasStringProp(node.props, "textColor", "#172033"),
          background: canvasStringProp(node.props, "fill", "#ffffff"),
          fontSize: canvasNumberProp(node.props, "fontSize", 16), lineHeight: 1.45,
          fontFamily: canvasStringProp(node.props, "fontFamily", "system-ui, sans-serif"),
          ...canvasTextStyle(node),
        }}
      />
    </foreignObject>
  );
}

export interface CanvasNodeViewProps {
  node: CanvasNode;
  selected: boolean;
  editing: boolean;
  readOnly: boolean;
  referenceResolution?: ReferenceResolution | undefined;
  referencePending: boolean;
  referenceResolverAvailable?: boolean;
  assetResolver?: CanvasAssetResolver | undefined;
  onPointerDown(node: CanvasNode, event: PointerEvent<SVGGElement>): void;
  onContextMenu?(node: CanvasNode, event: MouseEvent<SVGGElement>): void;
  onResizePointerDown(
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: PointerEvent<SVGCircleElement>,
  ): void;
  onRotatePointerDown(
    node: CanvasNode,
    event: PointerEvent<SVGCircleElement>,
  ): void;
  onResizeKeyDown(
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: KeyboardEvent<SVGCircleElement>,
  ): void;
  onRotateKeyDown(
    node: CanvasNode,
    event: KeyboardEvent<SVGCircleElement>,
  ): void;
  onStartEditing(node: CanvasNode): void;
  onCommitText(node: CanvasNode, text: string): void;
  onCancelEditing(): void;
  onReferenceActivate?: ((node: CanvasNode) => void) | undefined;
}

export function CanvasNodeView({
  node,
  selected,
  editing,
  readOnly,
  referenceResolution,
  referencePending,
  referenceResolverAvailable = true,
  assetResolver,
  onPointerDown,
  onContextMenu,
  onResizePointerDown,
  onRotatePointerDown,
  onResizeKeyDown,
  onRotateKeyDown,
  onStartEditing,
  onCommitText,
  onCancelEditing,
  onReferenceActivate,
}: CanvasNodeViewProps) {
  const messages = useCanvasMessages();
  const [focused, setFocused] = useState(false);
  const editableText =
    node.type === "sticky"
      ? canvasStringProp(node.props, "text", "")
      : node.type === "text"
        ? canvasStringProp(node.props, "text", "")
        : node.type === "shape"
          ? canvasStringProp(node.props, "label", "")
          : undefined;
  const locked = node.props.locked === true;

  if (editing && editableText !== undefined) {
    return (
      <InlineTextEditor
        node={node}
        value={editableText}
        onCommit={(text) => onCommitText(node, text)}
        onCancel={onCancelEditing}
      />
    );
  }

  const ariaLabel =
    node.type === "reference" && node.reference !== undefined
      ? messages.referenceLabel(referenceTargetKey(node.reference))
      : node.type === "image" &&
          typeof node.props.alt === "string" &&
          node.props.alt.trim().length > 0
        ? node.props.alt
        : messages.nodeLabel(node.type);
  return (
    <g
      data-canvas-node-id={node.id}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={selected}
      aria-disabled={locked}
      aria-keyshortcuts="Enter F2 ArrowLeft ArrowRight ArrowUp ArrowDown Alt+ArrowLeft Alt+ArrowRight Alt+ArrowUp Alt+ArrowDown Alt+R"
      transform={rotation(node)}
      onPointerDown={(event) => onPointerDown(node, event)}
      onContextMenu={(event) => onContextMenu?.(node, event)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
      onDoubleClick={() => {
        if (!readOnly && !locked && (editableText !== undefined || node.type === "diagram")) onStartEditing(node);
      }}
      onKeyDown={(event) => {
        if (
          (event.key === "Enter" || event.key === "F2") &&
          !readOnly && !locked &&
          (editableText !== undefined || node.type === "diagram")
        ) {
          event.preventDefault();
          onStartEditing(node);
        }
      }}
      style={{ cursor: readOnly ? "default" : locked ? "not-allowed" : "move", outline: "none" }}
    >
      {(focused || selected) && (
        <rect
          x={node.geometry.x - 5}
          y={node.geometry.y - 5}
          width={node.geometry.width + 10}
          height={node.geometry.height + 10}
          rx={10}
          fill="none"
          stroke={focused ? "#0f766e" : "#4f46e5"}
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          pointerEvents="none"
          aria-hidden="true"
        />
      )}
      <g opacity={typeof node.props.opacity === "number" ? Math.max(0.05, Math.min(1, node.props.opacity)) : 1}>
      <CanvasNodeContent
        node={node}
        selected={selected}
        referenceResolution={referenceResolution}
        referencePending={referencePending}
        referenceResolverAvailable={referenceResolverAvailable}
        assetResolver={assetResolver}
        onReferenceActivate={onReferenceActivate}
      />
      </g>
      {locked ? (
        <text
          x={node.geometry.x + node.geometry.width - 10}
          y={node.geometry.y + 17}
          textAnchor="end"
          fontSize={13}
          aria-hidden="true"
        >🔒</text>
      ) : null}
      {selected &&
        !readOnly && !locked &&
        (["nw", "ne", "sw", "se"] as const).map((handle) => {
          const west = handle.includes("w");
          const north = handle.includes("n");
          return (
            <circle
              key={handle}
              data-canvas-resize-handle={handle}
              cx={
                west ? node.geometry.x : node.geometry.x + node.geometry.width
              }
              cy={
                north ? node.geometry.y : node.geometry.y + node.geometry.height
              }
              r={6}
              fill="#ffffff"
              stroke="#4f46e5"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
              aria-label={`${messages.nodeLabel(node.type)} ${handle}`}
              role="button"
              tabIndex={0}
              aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Shift+ArrowLeft Shift+ArrowRight Shift+ArrowUp Shift+ArrowDown"
              onPointerDown={(event) =>
                onResizePointerDown(node, handle, event)
              }
              onKeyDown={(event) => onResizeKeyDown(node, handle, event)}
              style={{
                cursor:
                  handle === "nw" || handle === "se"
                    ? "nwse-resize"
                    : "nesw-resize",
              }}
            />
          );
        })}
      {selected && !readOnly && !locked && (
        <>
          <line
            x1={node.geometry.x + node.geometry.width / 2}
            y1={node.geometry.y}
            x2={node.geometry.x + node.geometry.width / 2}
            y2={node.geometry.y - 24}
            stroke="#4f46e5"
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            aria-hidden="true"
          />
          <circle
            data-canvas-rotate-handle
            cx={node.geometry.x + node.geometry.width / 2}
            cy={node.geometry.y - 24}
            r={7}
            fill="#ffffff"
            stroke="#4f46e5"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            aria-label={messages.rotateHandle}
            role="button"
            tabIndex={0}
            aria-keyshortcuts="ArrowLeft ArrowRight Shift+ArrowLeft Shift+ArrowRight"
            onPointerDown={(event) => onRotatePointerDown(node, event)}
            onKeyDown={(event) => onRotateKeyDown(node, event)}
            style={{ cursor: "grab" }}
          />
        </>
      )}
    </g>
  );
}
